// src/components/StoreManagement.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import useStoreManagement from '../hooks/useStoreManagement';
import { 
  createStore, 
  updateStore, 
  assignStoreManager, 
  removeStoreManager,
  STORE_TYPES,
  STORE_STATUS 
} from '../lib/storeManagement';
import { ToastContext } from './ToastProvider';

export default function StoreManagement() {
  const { user } = useAuth();
  const { notify } = React.useContext(ToastContext);
  const {
    stores,
    managedStores,
    loading,
    error,
    isUserStoreManager,
    isUserAdmin,
    isUserStoreManagerOrAdmin,
    storeManagerRole,
    userStoreType,
    loadAllStores,
    loadManagedStores,
    checkStoreManagementPermission
  } = useStoreManagement();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: STORE_TYPES.COOPERATIVE,
    description: '',
    address: '',
    phone: '',
    email: '',
    operatingHours: {
      monday: { open: '08:00', close: '17:00', closed: false },
      tuesday: { open: '08:00', close: '17:00', closed: false },
      wednesday: { open: '08:00', close: '17:00', closed: false },
      thursday: { open: '08:00', close: '17:00', closed: false },
      friday: { open: '08:00', close: '17:00', closed: false },
      saturday: { open: '08:00', close: '17:00', closed: false },
      sunday: { open: '08:00', close: '17:00', closed: false }
    }
  });

  // Load stores on component mount
  useEffect(() => {
    if (isUserStoreManagerOrAdmin) {
      if (isUserAdmin) {
        // Admin sees all stores
        loadAllStores();
      } else {
        // Store manager sees only their managed stores
        loadManagedStores();
      }
    } else {
      loadAllStores();
    }
  }, [isUserStoreManagerOrAdmin, isUserAdmin]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingStore) {
        // Update existing store
        const result = await updateStore(editingStore.id, formData);
        if (result.success) {
          notify('Store updated successfully!', { type: 'success' });
          setEditingStore(null);
          setShowCreateForm(false);
          resetForm();
          loadAllStores();
        } else {
          notify(`Failed to update store: ${result.error}`, { type: 'error' });
        }
      } else {
        // Create new store
        const result = await createStore(formData);
        if (result.success) {
          notify('Store created successfully!', { type: 'success' });
          setShowCreateForm(false);
          resetForm();
          loadAllStores();
        } else {
          notify(`Failed to create store: ${result.error}`, { type: 'error' });
        }
      }
    } catch (error) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: STORE_TYPES.COOPERATIVE,
      description: '',
      address: '',
      phone: '',
      email: '',
      operatingHours: {
        monday: { open: '08:00', close: '17:00', closed: false },
        tuesday: { open: '08:00', close: '17:00', closed: false },
        wednesday: { open: '08:00', close: '17:00', closed: false },
        thursday: { open: '08:00', close: '17:00', closed: false },
        friday: { open: '08:00', close: '17:00', closed: false },
        saturday: { open: '08:00', close: '17:00', closed: false },
        sunday: { open: '08:00', close: '17:00', closed: false }
      }
    });
  };

  const handleEditStore = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name || '',
      type: store.type || STORE_TYPES.COOPERATIVE,
      description: store.description || '',
      address: store.address || '',
      phone: store.phone || '',
      email: store.email || '',
      operatingHours: store.settings?.operatingHours || formData.operatingHours
    });
    setShowCreateForm(true);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingStore(null);
    resetForm();
  };

  const getStoreTypeLabel = (type) => {
    switch (type) {
      case STORE_TYPES.COOPERATIVE:
        return 'Cooperative Store';
      case STORE_TYPES.MEMBER:
        return 'Member Store';
      case STORE_TYPES.ESTABLISHMENT:
        return 'Establishment Store';
      default:
        return type;
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      [STORE_STATUS.ACTIVE]: 'bg-green-100 text-green-800',
      [STORE_STATUS.INACTIVE]: 'bg-gray-100 text-gray-800',
      [STORE_STATUS.SUSPENDED]: 'bg-red-100 text-red-800',
      [STORE_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {isUserStoreManagerOrAdmin ? (isUserAdmin ? 'Store Management (Admin)' : 'My Stores') : 'Store Management'}
          </h2>
          <p className="text-gray-600 mt-1">
            {isUserAdmin 
              ? 'Manage all stores in the system'
              : isUserStoreManager 
                ? `Manage your ${userStoreType} stores`
                : 'Manage all stores in the system'
            }
          </p>
        </div>
        
        {(isUserAdmin || !isUserStoreManager) && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
          >
            Create New Store
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Store Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingStore ? 'Edit Store' : 'Create New Store'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Store Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Store Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value={STORE_TYPES.COOPERATIVE}>Cooperative Store</option>
                  <option value={STORE_TYPES.MEMBER}>Member Store</option>
                  <option value={STORE_TYPES.ESTABLISHMENT}>Establishment Store</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                {editingStore ? 'Update Store' : 'Create Store'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stores List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {isUserAdmin ? 'All Stores' : isUserStoreManager ? 'My Managed Stores' : 'All Stores'}
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {(isUserAdmin ? stores : isUserStoreManager ? managedStores : stores).map((store) => (
            <div key={store.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-lg font-medium text-gray-900">{store.name}</h4>
                    {getStatusBadge(store.status)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{getStoreTypeLabel(store.type)}</p>
                  {store.description && (
                    <p className="text-sm text-gray-500 mt-1">{store.description}</p>
                  )}
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    {store.address && <span>📍 {store.address}</span>}
                    {store.phone && <span>📞 {store.phone}</span>}
                    {store.email && <span>📧 {store.email}</span>}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {(isUserAdmin || !isUserStoreManager) && (
                    <button
                      onClick={() => handleEditStore(store)}
                      className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => window.open(`/store/${store.id}`, '_blank')}
                    className="px-3 py-1 text-sm font-medium text-green-600 hover:text-green-800"
                  >
                    View Store
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {(isUserAdmin ? stores : isUserStoreManager ? managedStores : stores).length === 0 && (
          <div className="px-6 py-8 text-center">
            <p className="text-gray-500">
              {isUserAdmin 
                ? 'No stores found.'
                : isUserStoreManager 
                  ? 'You are not managing any stores yet.'
                  : 'No stores found.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
